/**
 * AsyncBoundary — loading + error + empty in one primitive (UX-5,
 * design-system universal kit).
 *
 * Every async surface renders the same three non-content states through
 * this one component instead of hand-rolling them (the "loading is not
 * systemic" review finding). Branch precedence: loading > error > empty >
 * children — while a refetch is in flight the skeleton wins, a failed
 * load never shows a stale empty-state, and only a settled, successful,
 * non-empty query renders children.
 *
 * Composes the existing primitives: Skeleton (loading), ErrorBanner
 * (error, with retry wiring), and a branded token-only empty state whose
 * copy is tenant-neutral by default (no counts, columns, or roadmap —
 * the UX-1 discipline).
 */

import type { ReactNode } from 'react';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorBanner } from '@/components/ErrorBanner';

export interface AsyncBoundaryProps {
  /** The thing this boundary loads, as the screen calls it ("your day",
   *  "the caseload"); the error banner names it. */
  what?: string;
  isLoading: boolean;
  isError: boolean;
  /** The error to describe when isError (forwarded to ErrorBanner). */
  error?: unknown;
  /** Offered on 5xx/network errors (forwarded to ErrorBanner). */
  onRetry?: () => void;
  /** Settled-and-successful but nothing to show → branded empty state. */
  isEmpty?: boolean;
  /** Empty-state copy overrides; defaults are tenant-neutral. */
  emptyHeadline?: string;
  emptyBody?: string;
  /** Loading override; defaults to a 3-row skeleton list. */
  skeleton?: ReactNode;
  children: ReactNode;
}

export function AsyncBoundary({
  isLoading,
  isError,
  error,
  onRetry,
  what,
  isEmpty = false,
  emptyHeadline = 'Nothing here yet.',
  emptyBody = '',
  skeleton,
  children,
}: AsyncBoundaryProps): ReactNode {
  if (isLoading) {
    return (
      <div role="status" aria-label="Loading" data-testid="async-boundary-loading">
        {skeleton ?? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}
      </div>
    );
  }
  if (isError) {
    return <ErrorBanner error={error} onRetry={onRetry} what={what} />;
  }
  if (isEmpty) {
    return (
      <div className="text-center py-8" data-testid="async-boundary-empty">
        <p className="text-sm font-medium text-ink">{emptyHeadline}</p>
        {emptyBody && (
          <p className="text-xs text-ink-soft mt-2 max-w-md mx-auto leading-relaxed">
            {emptyBody}
          </p>
        )}
      </div>
    );
  }
  return children;
}
